<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterRevVardocStore} from "@/charter/stores/charter-rev-vardoc-store.ts";
import {RevVardocCreateRequest} from "@/api/client/models/index.ts";

const revVardocStore = useCharterRevVardocStore();

const myTheme = themeQuartz.withParams({});

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
    filter: true,
  },
  columnDefs: [
    { field: "route" },
    { field: "revenueRotation", headerName: "Revenue per rotation" },
    { field: "revenueLeg", headerName: "Revenue per leg" },
    { field: "vardocPerLeg", headerName: "Vardoc per leg" },
    { field: "clientId", headerName: "Client" },
    { field: "dayOfWeek", headerName: "Days of the week" },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);
const showAddForm = ref(false);
const newRevVardoc = ref<RevVardocCreateRequest>({
  route: "",
  revenueRotation: 0,
  revenueLeg: 0,
  vardocPerLeg: 0,
  clientId: 0,
  dayOfWeek: "",
  isActive: true
});
const isSubmitting = ref(false);
const formError = ref<string | null>(null);

onMounted(async () => {
  await revVardocStore.fetchRevVardocs();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

async function refreshData() {
  await revVardocStore.fetchRevVardocs();
}

function toggleAddForm() {
  showAddForm.value = !showAddForm.value;
  if (showAddForm.value) {
    // Reset form when opening
    newRevVardoc.value = {
      route: "",
      revenueRotation: 0,
      revenueLeg: 0,
      vardocPerLeg: 0,
      clientId: 0,
      dayOfWeek: "",
      isActive: true
    };
    formError.value = null;
  }
}

async function submitNewRevVardoc() {
  if (!newRevVardoc.value.route || !newRevVardoc.value.dayOfWeek || newRevVardoc.value.clientId <= 0) {
    formError.value = "Please fill in all required fields";
    return;
  }

  try {
    isSubmitting.value = true;
    formError.value = null;
    await revVardocStore.addRevVardoc(newRevVardoc.value);
    // Reset form after successful submission
    newRevVardoc.value = {
      route: "",
      revenueRotation: 0,
      revenueLeg: 0,
      vardocPerLeg: 0,
      clientId: 0,
      dayOfWeek: "",
      isActive: true
    };
    // Close the form
    showAddForm.value = false;
  } catch (error) {
    formError.value = error instanceof Error ? error.message : "Failed to add revenue variable document";
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2>Revenue Variable Documents</h2>
      <div class="flex gap-2">
        <p-button label="Add RevVardoc" icon="pi pi-plus" severity="primary" outlined @click="toggleAddForm" />
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" outlined @click="refreshData" />
      </div>
    </div>

    <!-- Add RevVardoc Form -->
    <div v-if="showAddForm" class="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <h3 class="mb-3 mt-0">Add New Revenue Variable Document</h3>
      <div v-if="formError" class="mb-3 rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">{{ formError }}</div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div class="space-y-2">
          <label for="route" class="block mb-2">Route*</label>
          <p-input-text id="route" v-model="newRevVardoc.route" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="revenueRotation" class="block mb-2">Revenue per Rotation*</label>
          <p-input-number id="revenueRotation" v-model="newRevVardoc.revenueRotation" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="revenueLeg" class="block mb-2">Revenue per Leg*</label>
          <p-input-number id="revenueLeg" v-model="newRevVardoc.revenueLeg" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="vardocPerLeg" class="block mb-2">Vardoc per Leg*</label>
          <p-input-number id="vardocPerLeg" v-model="newRevVardoc.vardocPerLeg" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="clientId" class="block mb-2">Client ID*</label>
          <p-input-number id="clientId" v-model="newRevVardoc.clientId" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="dayOfWeek" class="block mb-2">Days of the Week*</label>
          <p-input-text id="dayOfWeek" v-model="newRevVardoc.dayOfWeek" class="w-full" :disabled="isSubmitting" />
        </div>
      </div>
      <div class="mt-3 flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" outlined @click="toggleAddForm" :disabled="isSubmitting" />
        <p-button label="Save" icon="pi pi-save" @click="submitNewRevVardoc" :loading="isSubmitting" />
      </div>
    </div>

    <AgGridVue
      :rowData="revVardocStore.revVardocs"
      :loading="revVardocStore.isLoading"
      :gridOptions="gridOptions"
      :theme="myTheme"
      class="ag-grid-container"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
.ag-grid-container {
  width: 100%;
  height: 400px;
}

h2, h3 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

label {
  font-weight: 500;
  color: var(--text-color-secondary);
}
</style>
